/**
 * Service worker registration and update prompt state.
 *
 * Updates never reload under the user (that is how people lose half-filled
 * forms): a new build waits until they choose "Reload". Registration failure
 * is non-fatal — the app still works online, it just cannot open offline.
 * Not registered in development (Vite's module graph must not be cached).
 */
import { useSyncExternalStore } from 'react';

import { cacheLoadedAssets } from './asset-cache';

type PwaState = { needRefresh: boolean; offlineReady: boolean };

let state: PwaState = { needRefresh: false, offlineReady: false };
const listeners = new Set<() => void>();
let updateServiceWorker: ((reload?: boolean) => Promise<void>) | null = null;

function set(patch: Partial<PwaState>): void {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

export function usePwaState(): PwaState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
    () => state,
  );
}

export async function registerServiceWorker(): Promise<void> {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) return;
  try {
    const { registerSW } = await import('virtual:pwa-register');
    updateServiceWorker = registerSW({
      onNeedRefresh: () => set({ needRefresh: true }),
      onOfflineReady: () => set({ offlineReady: true }),
      onRegisteredSW: (_url, registration) => {
        cacheLoadedAssets();
        // Check for a new build hourly while the app stays open on site.
        if (registration) setInterval(() => void registration.update(), 60 * 60_000);
      },
    });
  } catch {
    // No service worker: online-only, which is still a working app.
  }
}

export async function applyUpdate(): Promise<void> {
  await updateServiceWorker?.(true);
}

export function dismissUpdate(): void {
  set({ needRefresh: false });
}

export type StoragePersistence = 'persisted' | 'best-effort' | 'unsupported';

/**
 * Ask the browser not to evict our IndexedDB under storage pressure. Granted
 * heuristically (installed PWA, engagement); we ask once per load and report
 * the answer in the Sync Center.
 */
export async function requestPersistentStorage(): Promise<StoragePersistence> {
  try {
    if (!navigator.storage?.persist) return 'unsupported';
    if (await navigator.storage.persisted()) return 'persisted';
    return (await navigator.storage.persist()) ? 'persisted' : 'best-effort';
  } catch {
    return 'unsupported';
  }
}

export type StorageEstimateSummary = { usage: number; quota: number };

/** How much this app stores on the device and how much the browser allows. */
export async function estimateStorage(): Promise<StorageEstimateSummary | null> {
  try {
    if (!navigator.storage?.estimate) return null;
    const { usage, quota } = await navigator.storage.estimate();
    return typeof usage === 'number' && typeof quota === 'number' ? { usage, quota } : null;
  } catch {
    return null;
  }
}
